import { PaymentMethod as AdyenPaymentMethod } from '@adyen/adyen-web/dist/types/types';

import { flipObject } from '@utils/utils';

export type Environment = 'local' | 'dev' | 'prod';
export type PaymentMethodStatus = 'default' | 'selected' | 'error' | 'disabled' | 'focus';
export type Locale =
  | 'bg-BG'
  | 'cs-CZ'
  | 'da-DK'
  | 'de-AT'
  | 'de-CH'
  | 'de-DE'
  | 'el-GR'
  | 'en'
  | 'en-GB'
  | 'en-IE'
  | 'es-ES'
  | 'fi-FI'
  | 'fr-BE'
  | 'fr-CH'
  | 'fr-FR'
  | 'hr-HR'
  | 'hu-HU'
  | 'it-IT'
  | 'nl-NL'
  | 'nb-NO'
  | 'no-NO'
  | 'pl-PL'
  | 'pt-PT'
  | 'ro-RO'
  | 'ru-RU'
  | 'sk-SK'
  | 'sl-SI'
  | 'sv-SE'
  | 'zu';

export type ShopBundle = 'zooplus' | 'bitiba';

export function isShopBundle(value: unknown): value is ShopBundle {
  return value === 'zooplus' || value === 'bitiba';
}

export type ContainerType = 'registration' | 'myaccount' | 'default';

export type CustomerType = 'GUEST_CUSTOMER' | 'EXISTING_CUSTOMER' | 'NEW_CUSTOMER';

export function isCustomerType(value: unknown): value is CustomerType {
  return value === 'GUEST_CUSTOMER' || value === 'EXISTING_CUSTOMER' || value === 'NEW_CUSTOMER';
}

export const supportedPaymentMethods = {
  AK: 'ADYEN_CREDIT_CARD',
  AP: 'APPLE_PAY',
  BANCONTACT_MOBILE: 'BANCONTACT_MOBILE',
  BC: 'BANCONTACT_CARD',
  BM: 'BANCONTACT_MOBILE',
  BK: 'BRAINTREE_CREDIT_CARD',
  BS: 'DIRECT_DEBIT_SEPA',
  CD: 'CASH_ON_DELIVERY',
  DP: 'DOT_PAY',
  ID: 'IDEAL',
  KL: 'KLARNA_PAY_LATER',
  KO: 'KLARNA_PAY_OVER_TIME',
  MB: 'MULTIBANCO',
  OP: 'ONLINE_BANKING_PL',
  PP: 'PAYPAL',
  PT: 'PAYTRAIL',
  R: 'INVOICE',
  TW: 'TWINT',
  TY: 'TRUSTLY',
  VK: 'PREPAYMENT',
};

export const flippedSupportedPMs = flipObject(supportedPaymentMethods);
export type PaymentMethod = keyof typeof supportedPaymentMethods;
export function isPaymentMethod(value: unknown): value is PaymentMethod {
  return (
    typeof value === 'string' &&
    typeof (supportedPaymentMethods as Record<string, unknown>)[value] !== 'undefined'
  );
}

export type PaymentMethodInfo = {
  paymentMethod: PaymentMethod;
  status?: PaymentMethodStatus;
  availableForGuest?: boolean;
  cards?: CreditCardData[];
  lastIssuer?: Issuer;
  issuers?: Issuer[];
  bankAccountDetails?: SepaData;
  autoshipmentEligible?: boolean;
  paymentSetupInfo?: PaymentSetupInfo;
};

export type PaymentSetupInfo = {
  clientInfo: {
    pspName: string;
    pspToken: string;
    merchantId: string;
  };
  fallbackPsp: true | false;
};

export type RetryPaymentMethod =
  | 'APPLE_PAY'
  | 'BANCONTACT'
  | 'BANCONTACT_MOBILE'
  | 'CREDIT_CARD'
  | 'DIRECT_DEBIT_SEPA'
  | 'DOTPAY'
  | 'ONLINE_BANKING_PL'
  | 'IDEAL'
  | 'INVOICE'
  | 'MULTIBANCO'
  | 'PAYPAL'
  | 'PAYTRAIL'
  | 'PREPAYMENT'
  | 'TRUSTLY'
  | 'TWINT'
  | 'KLARNA_PAY_LATER'
  | 'KLARNA_PAY_OVER_TIME';

// Lower case values of the ZPP-API enum: https://src.private.zooplus.net/projects/ZPP/repos/zpp-api/browse/src/main/java/com/zooplus/api/message/paymentmethods/creditcards/CreditCardBrand.java
// Which are also the values known by shop-checkout: https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/creditcards/CreditCardBrand.java
export type CreditCardBrand =
  | 'american_express'
  | 'carte_bancaire'
  | 'diners_club'
  | 'maestro'
  | 'master_card'
  | 'other'
  | 'visa'
  | 'bcmc';

// A map between CardType - CreditCardBrand. Used to get the CreditCardBrand from a given CardType
export const creditCardBrandOfCardType: Record<CardType, CreditCardBrand> = {
  visa: 'visa',
  mastercard: 'master_card',
  'american-express': 'american_express',
  'diners-club': 'diners_club',
  discover: 'other',
  jcb: 'other',
  unionpay: 'other',
  maestro: 'maestro',
  elo: 'other',
  mir: 'other',
  hiper: 'other',
  hipercard: 'other',
  cartebancaire: 'carte_bancaire',
  bcmc: 'bcmc',
};

// A map between CreditCardBrand-CardType. Used to get the CardType from a given CreditCardBrand
export const cardTypeOfCreditCardBrand: Record<CreditCardBrand, Nullable<CardType>> = {
  american_express: 'american-express',
  carte_bancaire: 'cartebancaire',
  diners_club: 'diners-club',
  maestro: 'maestro',
  master_card: 'mastercard',
  other: null,
  visa: 'visa',
  bcmc: 'bcmc',
};

// beware: these values are actually a weird mix of supported cards and non-supported cards (elo, mir, etc) and also non-matching values (different spelling and case) for both Adyen and Braintree
export const supportedCardTypes = {
  visa: 'visa',
  mastercard: 'mastercard',
  'american-express': 'american-express',
  'diners-club': 'diners-club',
  discover: 'discover',
  jcb: 'jcb',
  unionpay: 'unionpay',
  maestro: 'maestro',
  elo: 'elo',
  mir: 'mir',
  hiper: 'hiper',
  hipercard: 'hipercard',
  cartebancaire: 'cartebancaire',
  bcmc: 'bcmc',
};

export type CardType = keyof typeof supportedCardTypes;

export type RetryPaymentStatus =
  | 'SUCCESSFUL'
  | 'UNSUCCESSFUL'
  | 'CANCELLED'
  | 'PROCESSING'
  | 'UNKNOWN'
  | 'SERVICE_NOT_AVAILABLE';

export type ResultCode = 'REFUSED';

export type RetryPaymentState =
  | 'AUTHORIZATION_ERROR'
  | 'AUTHORIZATION_FAILED'
  | 'AUTHORIZATION_PENDING'
  | 'AUTHORIZATION_SENT'
  | 'AUTHORIZATION_SUCCESSFUL'
  | 'CANCELLATION_ERROR'
  | 'CANCELLATION_FAILED'
  | 'CANCELLATION_PENDING'
  | 'CANCELLATION_SENT'
  | 'CANCELLATION_SUCCESSFUL'
  | 'CAPTURE_ERROR'
  | 'CAPTURE_FAILED'
  | 'CAPTURE_PENDING'
  | 'CAPTURE_SENT'
  | 'CAPTURE_SUCCESSFUL'
  | 'INITIATED_PENDING'
  | 'INITIATED_SUCCESSFUL'
  | 'REFUND_ERROR'
  | 'REFUND_FAILED'
  | 'REFUND_PENDING'
  | 'REFUND_SENT'
  | 'REFUND_SUCCESSFUL';

export type PspConnector = 'ADYEN' | 'BRAINTREE' | null;

export type Maybe<T> = T | undefined;

export type Nullable<T> = T | null;

export type MaybeNullable<T> = T | undefined | null;

export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

export type Additional3dsInfo = {
  email: string;
  shippingAddress: Address;
  billingAddress: Address;
  accountCreateDate: number; // timestamp
  creditCardCreateDate?: number; // timestamp
  creditCardHolderName?: string;
  accountId: number;
  ipAddress: string;
  articleIds: number[];
  userAgent: string;
};

export type CreditCardType =
  /* Customer uses card details and doesn't save it*/
  | 'ONE_TIME'
  /* Customer uses card details and saves it */
  | 'TO_SAVE'
  /* Customer uses saved card */
  | 'SAVED';

export type Address = {
  firstName: string;
  lastName: string;
  street: string;
  company: string | null;
  city: string;
  isoCountry: string;
  country: string;
  postalCode: string;
  phoneNumber: string | null;
};

export type LocalPaymentMethods = {
  paymentPartnerConnector: string;
  paymentMethods: ZppPaymentMethod[];
  countryCode: string;
};

// This type represents the local payment method as returned by zpp-api.
// In its processing of adyen local payment methods list, zpp-adyen-connector-consumer adds the payment method zpp name.
export type ZppPaymentMethod = { paymentMethod: RetryPaymentMethod } & AdyenPaymentMethod;

export function createAdyenPaymentMethod(zppPaymentMethod: ZppPaymentMethod): AdyenPaymentMethod {
  const { paymentMethod, ...adyenPaymentMethod } = zppPaymentMethod;
  return adyenPaymentMethod;
}

export enum BraintreeShippingNameIndicator {
  ACCOUNT_AND_SHIPPING_NAME_IDENTICAL_01 = '01',
  ACCOUNT_AND_SHIPPING_NAME_DIFFERENT_02 = '02',
}

export enum BraintreeAddressMatch {
  BILLING_AND_SHIPPING_ADDRESS_IDENTICAL_01 = 'Y',
  BILLING_AND_SHIPPING_ADDRESS_DIFFERENT_02 = 'N',
}

export enum BraintreeAuthenticationIndicator {
  PAYMENT_01 = '01',
  RECURRING_TRANSACTION_02 = '02',
  INSTALLMENT_03 = '03',
  ADD_CARD_04 = '04',
  MAINTAIN_CARD_05 = '05',
  CARDHOLDER_VERIFICATION_06 = '06',
}

export type BraintreeCustomFields = {
  card_type: CreditCardType;
  subscription_first: string;
  customer_id?: string;
  fraud_prevention_recommendation: FraudPreventionRecommendation | undefined;
  force_3ds_challenge_enabled: 'true' | 'false';
};

export type InitiateRetryPaymentRequestData = {
  orderId: string;
  customerId: string;
  tokenId: string;
  returnUrl: string;
  paymentMethod: RetryPaymentMethod;
};

export type InitiateRetryPaymentResponse = {
  orderId: number;
  paymentId: number;
  paymentMethod: RetryPaymentMethod;
  paymentState: RetryPaymentState;
  retryPaymentStatus: RetryPaymentStatus;
  connector: PspConnector;
};

export type Issuer = {
  issuerId: string | null;
  issuerName: string | null;
  issuerOrder: number | null;
  logoUrl: string | null;
};

export interface CreditCardData {
  pspIdentifier: string;
  holderName: Nullable<string>;
  brand: Nullable<CardType>;
  expirationMonth: Nullable<string>;
  expirationYear: Nullable<string>;
  selected: boolean;
  expired: boolean;
  lastFourDigits: Nullable<string>;
  nonce: Nullable<string>;
  psp?: string;
}

export type DefinedCreditCardData = {
  pspIdentifier: string;
  bin: string;
  holderName: Nullable<string>;
  brand: CardType;
  expirationMonth: string;
  expirationYear: string;
  selected?: boolean; // todo: really possible to be undefined?
  expired?: boolean;
  lastFourDigits: string;
  nonce: Nullable<string>;
};

//used for modernize checkout
export type CreditCard = {
  brand: CreditCardBrands; //
  pspIdentifier: string | null; //
  pspIdentifierAlternative: string | null;
  psp: string | null; //
  createdAt: number | null;
  maskedNumber: string | null; //
  bin: string | null; //
  holderName: string | null; //
  expirationMonth: string | null; //
  expirationYear: string | null; //
  expired: boolean; //
  favorite: boolean; //??
  nonce: string | null; //
};

export type CreditCardBrands =
  | 'MASTER_CARD'
  | 'VISA'
  | 'DINERS'
  | 'DINERS_CLUB'
  | 'AMERICAN_EXPRESS'
  | 'MAESTRO'
  | 'CARTE_BANCAIRE'
  | 'BCMC'
  | 'OTHER';
export type CreditCardStatus = 'loading' | 'idle';
export interface SepaData {
  holderName: string;
  lastFourDigits: string;
}

export type AppConfig = {
  shop: Shop;
  locale: Locale;
  realm: ShopBundle;
  environment: Environment;
};

export type Shop = {
  domain: string;
  locales: Locale[];
  siteId: number;
};

export enum Sizes {
  XXS = 320,
  XS = 374,
  SM = 544,
  MD = 768,
  LG = 1024,
}

export type FraudPreventionRecommendation = 'ALLOW' | 'REVIEW' | 'PREVENT' | null;
